import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import KnowledgeBaseHeader from '@/components/knowledge-base/KnowledgeBaseHeader';
import FeatureGuidesSidebar from '@/components/knowledge-base/FeatureGuidesSidebar';
import GuideDetail from '@/components/knowledge-base/GuideDetail';
import { FeatureGuide } from '@/types/knowledgeBase';
import { getSuperAdminFeatureGuides } from '@/data/superAdminFeatureGuides';
import { hasAccess } from '@/utils/permissionService';
import SuperAdminHeader from '@/components/superadmin/SuperAdminHeader';
import { useSuperAdminDashboard } from '@/hooks/useSuperAdminDashboard';

const SuperAdminKnowledgeBase = () => {
  const navigate = useNavigate();
  const { isLoading, user, handleLogout } = useSuperAdminDashboard();
  const [featureGuides, setFeatureGuides] = useState<FeatureGuide[]>([]);
  const [filteredGuides, setFilteredGuides] = useState<FeatureGuide[]>([]);
  const [selectedGuide, setSelectedGuide] = useState<FeatureGuide | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const guides = getSuperAdminFeatureGuides();
    setFeatureGuides(guides);
    setFilteredGuides(guides);
    if (guides.length > 0) {
      setSelectedGuide(guides[0]);
    }
  }, []);

  useEffect(() => {
    if (featureGuides.length > 0) {
      let filtered = [...featureGuides];
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          guide => 
            guide.title.toLowerCase().includes(term) || 
            guide.description.toLowerCase().includes(term) ||
            guide.content.toLowerCase().includes(term)
        );
      }
      
      if (activeTab !== 'all') {
        filtered = filtered.filter(guide => guide.category.toLowerCase() === activeTab.toLowerCase());
      }
      
      setFilteredGuides(filtered);
      
      if (filtered.length > 0 && selectedGuide && !filtered.some(guide => guide.id === selectedGuide.id)) {
        setSelectedGuide(filtered[0]);
      }
    }
  }, [searchTerm, activeTab, featureGuides, selectedGuide]);
  
  useEffect(() => {
    if (!isLoading && user) {
      const canAccess = user.role === 'superadmin' || hasAccess('super-admin-knowledge-base');
      if (!canAccess) {
        navigate('/super-admin/dashboard');
      }
    }
  }, [isLoading, user, navigate]);
  
  const categories = ['all', ...new Set(featureGuides.map(guide => guide.category.toLowerCase()))];

  const handleNavigateToSettings = () => {
    navigate('/super-admin/settings');
  };

  if (isLoading) {
    return (
      <AdminLayout
        isLoading={true}
        onLogout={handleLogout}
      >
        <div>Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      isLoading={false}
      onLogout={handleLogout}
      userRole="superadmin"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SuperAdminHeader 
          title="Knowledge Base" 
          description="Comprehensive guides for all SuperAdmin features and tools" 
          onNavigateToSettings={handleNavigateToSettings}
          userRole={user?.role}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <FeatureGuidesSidebar
            filteredGuides={filteredGuides}
            selectedGuide={selectedGuide}
            setSelectedGuide={setSelectedGuide}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            categories={categories}
          />
          
          <div className="md:col-span-2">
            <Card className="mb-4">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="relative w-full md:w-64">
                    <input
                      type="text"
                      placeholder="Search guides..."
                      className="w-full px-4 py-2 border rounded-md"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </Card>
            
            <GuideDetail 
              selectedGuide={selectedGuide}
              allGuides={featureGuides}
              onSelectGuide={setSelectedGuide}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SuperAdminKnowledgeBase;
